import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Error } from 'src/app/models/error.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { MaintenanceListFields } from 'src/app/models/maintenance-list-fields.models';
import { Router } from '@angular/router';
import { Service } from 'src/app/models/service.model';
import { Services } from 'src/app/services/services/services.service';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/services/users/users.service';
import { entAnimations } from 'src/app/animations';
import { Subscription, lastValueFrom } from 'rxjs';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { ServiceTranslated } from 'src/app/models/serviceTranslated';
import { RouteStatus } from 'src/app/models/services/maintenance/routeStatus.model';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { DISPLAYMODE } from 'src/app/models/user-settings.model';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { UserInfo } from 'src/app/models/user-info.model';
import { MatDialog } from '@angular/material/dialog';
import { TeamsClassAddDialogComponent } from '../exchange/teams/teams-class-add-dialog/teams-class-add-dialog.component';

@Component({
  selector: 'app-portail',
  templateUrl: './portail.component.html',
  styleUrls: ['./portail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class PortailComponent implements OnInit {
  isLoading = false;
  selectedUser: UserInfo = new UserInfo();
  currentLang: string;
  searchText: string;
  private techServices: Service[] = [];
  filterServicesTechTranslated: ServiceTranslated[] = [];
  maintenanceMessage: MaintenanceListFields[];
  display: number;

  isManageRolesDisabled = false;
  isUserAddDisabled = false;
  private data: RouteStatus[] = [];
  private subscription: Subscription = new Subscription();

  public get DisplayMaxi(): number {
    return DISPLAYMODE.Maxi;
  }

  public get DisplayMini(): number {
    return DISPLAYMODE.Mini;
  }

  public get DisplayList(): number {
    return DISPLAYMODE.List;
  }

  constructor(
    _translateService: TranslateService,
    private _userService: UsersService,
    private _settingsService: SettingsService,
    private _matDialog: MatDialog,
    private _router: Router,
    private _graphService: GraphService,
    private _services: Services,
    private _routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {
    this.getListofMaintenancesMessages();

    this.currentLang = _translateService.currentLang;

    _translateService.onLangChange.subscribe(l => {
      this.currentLang = l.lang;
      if (this.filterServicesTechTranslated) {
        this.filterServicesTechTranslated = this.getFilterServicesTranslated();
      }
    });
  }

  private getFilterServicesTranslated(): ServiceTranslated[] {
    const isLangGerman = this.currentLang === 'de';
    return this.techServices.map(service => ({
      oid: service.oid,
      name: service.name,
      jpegPhoto: service.jpegPhoto,
      title: isLangGerman ? service.title_de : service.title_fr,
      description: isLangGerman
        ? service.description_de
        : service.description_fr,
      help: isLangGerman ? service.help_de : service.help_fr,
      accessurl: isLangGerman ? service.accessurl_de : service.accessurl_fr,
      priority: service.priority,
      subtype: service.subtype,
      subtitle: isLangGerman ? service.subtitle_de : service.subtitle_fr,
      maintenance: service.maintenance,
    }));
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.searchText = '';

    this.getUser();

    //Maintenance mode settings
    const urls = ['/roles', '/users-add'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          this.isManageRolesDisabled = statuses['/roles'];
          this.isUserAddDisabled = statuses['/users-add'];
        })
    );
  }

  async getUserDisplayValue() {
    if (
      this._localStorageService.getItem<string>('userDefaultDisplayMode') ===
      null
    ) {
      await lastValueFrom(
        this._settingsService.getUserSettings(this.selectedUser.oid)
      ).then(us => {
        if (us !== null) {
          this.display = us.displayModeId;
        } else {
          this.display = DISPLAYMODE.Maxi;
        }
      });
    } else {
      this.display = this._localStorageService.getItem<number>(
        'userDefaultDisplayMode'
      );
    }
  }

  changeDisplay(displayValue: number) {
    this.display = displayValue;
    this._localStorageService.setItem('userDefaultDisplayMode', this.display);
    this.searchText = '';
  }

  onAccessButtonClick(event: { url: string; title: string }): void {
    this._services.openService(event.url, event.title);
  }

  onHelpButtonClick(url: string): void {
    this._services.openDocumentation(url);
  }

  findService(service_oid: string): Service {
    return this.selectedUser.services.find(
      service => service.oid === service_oid
    );
  }

  newTeams(): void {
    this._matDialog.open(TeamsClassAddDialogComponent, {
      panelClass: 'app-user-form-dialog',
      data: {
        action: 'new',
        currentUser: this.selectedUser,
      },
    });
  }

  private async getListofMaintenancesMessages(): Promise<void> {
    try {
      this.maintenanceMessage = await lastValueFrom(
        this._graphService.getListMaintenanceMessage()
      );
      // Filter location = Front page
      this.maintenanceMessage = this.maintenanceMessage.filter(
        x => x.location == 'Service'
      );

      this.maintenanceMessage = this.maintenanceMessage.filter(x =>
        this.techServices.some(service => service.oid === x.service_oid)
      );
    } catch {
      //Empty
    }
  }

  private async getUser(): Promise<void> {
    try {
      this.isLoading = true;
      // NO User in Cache
      if (this._localStorageService.getItem<UserInfo>('currentUser') === null) {
        this.selectedUser = await lastValueFrom(
          this._userService.getCurrentUser()
        );
      } else {
        this.selectedUser = this._userService.currentUserValue;
      }

      this.getUserDisplayValue();

      // Keep All Technical Services
      this.techServices = this.selectedUser.services.filter(element =>
        element.subtype.includes('technique')
      );

      this.techServices = this.techServices.sort((a: Service, b: Service) => {
        return a.priority - b.priority;
      });

      // Get maintenance settings from database
      this.data = await lastValueFrom(
        this._routeStatusService.getRouteStatuses()
      );
      if (this.data === null || this.data.length === 0) {
        this.data = Object.values(null);
      }

      this.techServices.forEach(service => {
        for (const prop in this.data) {
          if (this.data[prop].isDisabled) {
            const arrayServiceWord = service.accessurl_fr.split('/');
            const arrayUrlMaintenance = this.data[prop].url.split('/');

            if (
              arrayServiceWord[arrayServiceWord.length - 1] ===
              arrayUrlMaintenance[arrayUrlMaintenance.length - 1]
            ) {
              service.maintenance = true;
              break; // Early exit on match
            }
          }
        }
      });

      this.filterServicesTechTranslated = this.getFilterServicesTranslated();
      this.isLoading = false;
    } catch (error) {
      let customError: Error = { error: undefined };
      if (error.error) {
        customError = error.error;
      }
      if (customError.error !== undefined) {
        if (customError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }
}
